// The crafting panel's motion, as curves: the made item's flight from the bench to its tile, the
// count's pop when it lands, and the glow on a recipe that has just become makeable. The panel plays
// them on the renderer ticker against a layer that survives its rebuilds; the shapes live here so the
// one thing that matters most about each -- that it ENDS, and lands where it was sent -- is a test.
//
// Pure of pixi -- see __tests__/crafting_fx.test.ts.

// The flight: a quick pluck up out of the well, then a pull in to the tile that speeds up as it goes,
// shrinking to the tile's size. The pickup flight's shape (effects/pickup_flight.ts), panel-sized.
export const FLIGHT_MS = 520
export const FLIGHT_PLUCK = 0.22
const PLUCK_RISE = 9
const PLUCK_GROW = 1.15
// How far above the straight line the pull bows.
const BOW = 26
// Batches leave the bench one after another.
export const FLIGHT_STAGGER_MS = 70
export const MAX_BATCH_FLIGHTS = 6

// The count on a tile that something just landed on.
export const POP_MS = 320
const POP_PEAK = 1.45

// A recipe that has just become makeable, or the one a reveal or a jump sent the player to.
export const GLOW_MS = 1500

// Holding Craft: the first repeat waits, then each comes sooner, down to a floor.
export const HOLD_FIRST_MS = 380
export const HOLD_MIN_MS = 110
export const HOLD_ACCEL = 0.8

// The Pack tab's "+N" lingers this long after the last landing.
export const BADGE_MS = 2200

export interface Point {
    x: number
    y: number
}

const clamp01 = (t: number): number => Math.max(0, Math.min(1, t))
const easeOut = (t: number): number => 1 - Math.pow(1 - t, 3)

/**
 * Where a flight is `elapsed` ms in: position and scale (relative to its size at the start). Ends
 * exactly on `to` at `endScale`. `to` is asked for every frame by the caller, so a tile that moved
 * under a rebuild is still where the item lands.
 */
export const flightAt = (from: Point, to: Point, elapsed: number, endScale: number, duration = FLIGHT_MS): Point & { scale: number } => {
    const t = clamp01(elapsed / duration)
    const lifted = { x: from.x, y: from.y - PLUCK_RISE }
    if (t < FLIGHT_PLUCK) {
        const k = easeOut(t / FLIGHT_PLUCK)
        return { x: from.x, y: from.y - PLUCK_RISE * k, scale: 1 + (PLUCK_GROW - 1) * k }
    }
    const k = Math.pow((t - FLIGHT_PLUCK) / (1 - FLIGHT_PLUCK), 2.2)
    const control = { x: (lifted.x + to.x) / 2, y: Math.min(lifted.y, to.y) - BOW }
    const u = 1 - k
    return {
        x: u * u * lifted.x + 2 * u * k * control.x + k * k * to.x,
        y: u * u * lifted.y + 2 * u * k * control.y + k * k * to.y,
        scale: PLUCK_GROW + (endScale - PLUCK_GROW) * k,
    }
}

/** A landed count's scale: up fast, back down, and exactly 1 once it is over. */
export const popScale = (elapsed: number, duration = POP_MS): number => {
    if (!(elapsed >= 0) || elapsed >= duration) {
        return 1
    }
    const t = elapsed / duration
    const RISE = 0.3
    return t < RISE ? 1 + (POP_PEAK - 1) * (t / RISE) : POP_PEAK - (POP_PEAK - 1) * easeOut((t - RISE) / (1 - RISE))
}

/**
 * A glow's brightness `elapsed` ms in: rises fast enough to beat the eye to the tile, holds, then
 * fades -- so it reads as "look here", never as a state the tile is now in. 0 outside its time.
 */
export const glowAlpha = (elapsed: number, duration = GLOW_MS): number => {
    if (!(elapsed >= 0) || elapsed >= duration) {
        return 0
    }
    const t = elapsed / duration
    const RISE = 0.08
    const FADE_FROM = 0.55
    if (t < RISE) {
        return t / RISE
    }
    if (t < FADE_FROM) {
        return 1
    }
    return (1 - t) / (1 - FADE_FROM)
}

/** The wait before the next repeat of a held Craft button. */
export const nextHoldDelay = (delay: number): number => Math.max(HOLD_MIN_MS, delay * HOLD_ACCEL)

/** A spark thrown off the bench: where it is, and how bright, `elapsed` ms after the craft. */
export const sparkAt = (origin: Point, angle: number, reach: number, elapsed: number, duration: number): Point & { alpha: number; scale: number } => {
    const t = clamp01(elapsed / duration)
    const k = easeOut(t)
    return {
        x: origin.x + Math.cos(angle) * reach * k,
        y: origin.y + Math.sin(angle) * reach * k - 6 * k,
        alpha: 1 - t,
        scale: 1 - 0.7 * t,
    }
}
